'use client';

import React, { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface StoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  story: {
    id: number;
    name: string;
    age: number;
    program: string;
    achievement: string;
    duration: string;
    image: string;
    alt: string;
    beforeImage?: string;
    beforeAlt?: string;
    afterImage?: string;
    afterAlt?: string;
    quote: string;
    fullStory: string;
    metrics: {
      label: string;
      value: string;
    }[];
    milestones: {
      month: string;
      achievement: string;
    }[];
  } | null;
}

const StoryModal = ({ isOpen, onClose, story }: StoryModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !story) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-primary/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-modal max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-heading font-bold text-primary">
            {story.name}&apos;s Success Story
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors duration-300"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <div className="p-6">
          {story.beforeImage && story.afterImage && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="relative h-64 rounded-lg overflow-hidden">
                <AppImage
                  src={story.beforeImage}
                  alt={story.beforeAlt || 'Before transformation photo'}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-primary/80 text-primary-foreground px-3 py-1 rounded-full text-xs font-heading font-bold">
                  Before
                </div>
              </div>
              <div className="relative h-64 rounded-lg overflow-hidden">
                <AppImage
                  src={story.afterImage}
                  alt={story.afterAlt || 'After transformation photo'}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-heading font-bold">
                  After
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-heading font-bold">
                {story.program}
              </div>
              <p className="text-sm text-text-secondary">
                Age {story.age} • {story.duration}
              </p>
            </div>
            <h3 className="text-xl font-heading font-bold text-primary mb-2">
              {story.achievement}
            </h3>
            <blockquote className="border-l-4 border-accent pl-4 py-2 mb-4 italic text-text-secondary">
              &quot;{story.quote}&quot;
            </blockquote>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {story.metrics.map((metric, index) => (
              <div key={index} className="bg-muted rounded-lg p-4 text-center">
                <p className="text-xs text-text-secondary mb-1">{metric.label}</p>
                <p className="text-2xl font-heading font-bold text-accent">
                  {metric.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-bold text-primary mb-3">
              The Journey
            </h4>
            <p className="text-text-primary leading-relaxed whitespace-pre-line">
              {story.fullStory}
            </p>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-bold text-primary mb-3">
              Key Milestones
            </h4>
            <div className="space-y-3">
              {story.milestones.map((milestone, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                    <Icon
                      name="CheckIcon"
                      size={16}
                      variant="solid"
                      className="text-accent-foreground"
                    />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-primary">
                      {milestone.month}
                    </p>
                    <p className="text-sm text-text-secondary">
                      {milestone.achievement}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center space-x-4 pt-6 border-t border-border">
            <button className="px-6 py-3 bg-accent text-accent-foreground font-heading font-bold rounded-md hover:bg-accent/90 transition-colors duration-300 flex items-center space-x-2">
              <Icon name="ShareIcon" size={18} variant="outline" />
              <span>Share Story</span>
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-muted text-text-primary font-heading font-semibold rounded-md hover:bg-muted/80 transition-colors duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryModal;